// eslint-disable-next-line @altermaind/you-shall-not-pass
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge, RenderLocalesWithComponents } from '@/_shared/components';
import { Form } from '@/_shared/components/Form';
import { WizardNavigationButton } from '@/_shared/components/Wizard/types';
import { getDocLink } from '@/_shared/constants';
import { convertSizeToNumber } from '@/_shared/helpers';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useNotification } from '@/_shared/hooks/useNotification';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { useWizard } from '@/_shared/hooks/useWizard';
import { getDelegatesCall, postCreateDelegateGuestCall } from '@/organization/state';
import { AddedDelegatesList } from './AddedDelegatesList';

const formDefaultValues = {
  firstName: '',
  lastName: '',
  email: '',
};

type Props = { wizardNavigationButton: WizardNavigationButton };

export const AddDelegate: FC<Props> = ({ wizardNavigationButton }) => {
  const t = useTranslations('configuration.hr.delegate.confirm');
  const sharedT = useTranslations();
  const { execute: executePostCreateDelegateGuest, loading: loadingPostCreateDelegateGuest } =
    postCreateDelegateGuestCall.hook();
  const { execute: executeGetDelegates, hrDelegates } = getDelegatesCall.hook();
  const { setNavigationButtonStatus, resetWizardNavigationButtonStatus } = useWizard();
  const gapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`);
  const gapXXL = convertSizeToNumber(`${useThemeValue('sizes', 'xxl')}`);
  const { genericResponsiveValue } = useResponsiveStyle();
  const { isDesktop } = useBreakingPoints();
  const { notify } = useNotification();
  const { openDialog: openContacts } = useDialog('contacts');

  const {
    control,
    watch,
    handleSubmit,
    formState: { isValid, errors: formErrors, touchedFields },
    reset,
  } = useForm({
    defaultValues: formDefaultValues,
    // onChange keeps isValid reactive so the CTA enables while typing;
    // field error messages are still gated on blur via touchedFields below.
    mode: 'onChange',
  });
  const fieldValues = watch();

  const someInputFilled = useMemo(() => {
    return Object.keys(fieldValues).some(
      (key: string) => !!fieldValues[key as keyof typeof fieldValues],
    );
  }, [fieldValues]);

  const goToDocs = useCallback(() => {
    window.open(getDocLink('ADMIN_AND_DELEGATES_REVIEW_AND_CONFIRM'), '_blank');
  }, []);

  const goToContact = useCallback(() => {
    openContacts({});
  }, [openContacts]);

  const onAdd = useCallback(() => {
    if (isValid) {
      const { firstName, lastName, email } = fieldValues;
      executePostCreateDelegateGuest({
        firstName,
        lastName,
        email,
        successSaga: () => {
          executeGetDelegates({
            start: 0,
            length: 1000,
          });
          reset(formDefaultValues);
        },
        errorSaga: () => {
          notify(sharedT('errors.genericApiError'), 'error');
        },
      });
    }
  }, [
    executeGetDelegates,
    executePostCreateDelegateGuest,
    fieldValues,
    isValid,
    notify,
    reset,
    sharedT,
  ]);

  useEffect(() => {
    // Update the navigation button status based on form validity
    setNavigationButtonStatus({
      disableNextButton: hrDelegates.length === 0 || loadingPostCreateDelegateGuest,
      isLoadingNextButton: loadingPostCreateDelegateGuest,
    });

    return () => {
      // Cleanup function to reset the navigation button status
      resetWizardNavigationButtonStatus();
    };
  }, [
    hrDelegates.length,
    isValid,
    loadingPostCreateDelegateGuest,
    resetWizardNavigationButtonStatus,
    setNavigationButtonStatus,
    someInputFilled,
  ]);

  return (
    <Box mb='s'>
      <Box
        display='flex'
        flex={10}
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        style={{ gap: gapXXL }}
      >
        <Box
          width='100%'
          display='flex'
          flexDirection='column'
          justifyContent='left'
          alignItems='left'
          textAlign='left'
          flex={genericResponsiveValue({
            breakPoints: { desktop: 6 },
            defaultValue: 10,
          })}
        >
          <Box mb='m'>
            <Typography
              data-testid='add-delegate-title'
              variant='h1'
              color='primary/text'
              fontSize={genericResponsiveValue({
                breakPoints: { desktop: 50 as never },
                defaultValue: 'xxxl',
              })}
              lineHeight={genericResponsiveValue({
                breakPoints: { desktop: '60px' as never },
                defaultValue: 'xxxl',
              })}
              fontWeight='bold'
              mb={genericResponsiveValue({
                breakPoints: { desktop: 'm' },
                defaultValue: 'xs',
              })}
            >
              {t('title')}
            </Typography>
            <Typography variant='h4' fontWeight={200} mt='s' color='primary/text'>
              {t('subtitle')}
            </Typography>
          </Box>
          <Box
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
            display='flex'
            style={{ gap: gapXS }}
          >
            <Box
              display={genericResponsiveValue({
                breakPoints: { desktop: 'grid' },
                defaultValue: 'flex',
              })}
              flexDirection={genericResponsiveValue({
                breakPoints: { desktop: 'row' },
                defaultValue: 'column',
              })}
              justifyContent='space-between'
              style={{ gap: gapXS }}
              gridTemplateColumns={`repeat(${genericResponsiveValue({
                breakPoints: { desktop: 2 },
                defaultValue: 1,
              })}, 1fr)`}
              width='100%'
            >
              <Box width='100%' flex={1}>
                <Form.FormInput
                  // @ts-ignore
                  control={control}
                  additonalProps={{
                    required: true,
                  }}
                  avoidRequiredStarOnLabel
                  name='firstName'
                  data-testid='add-delegate-first-name'
                  placeholder={t('addDelegate.firstNamePlaceholder')}
                  label={t('addDelegate.firstName')}
                  autoComplete='off'
                  hasError={!!formErrors.firstName && !!touchedFields.firstName}
                />
              </Box>
              <Box width='100%' flex={1}>
                <Form.FormInput
                  // @ts-ignore
                  control={control}
                  additonalProps={{
                    required: true,
                  }}
                  avoidRequiredStarOnLabel
                  data-testid='add-delegate-last-name'
                  name='lastName'
                  placeholder={t('addDelegate.lastNamePlaceholder')}
                  label={t('addDelegate.lastName')}
                  autoComplete='off'
                  hasError={!!formErrors.lastName && !!touchedFields.lastName}
                />
              </Box>
              <Box width='100%' gridColumn='1/span 2' flex={1}>
                <Form.FormInput
                  // @ts-ignore
                  control={control}
                  additonalProps={{
                    required: true,
                    pattern: {
                      value: /\S+@\S+\.\S+/,
                      message: 'Entered value does not match email format',
                    },
                  }}
                  avoidRequiredStarOnLabel
                  data-testid='add-delegate-email'
                  name='email'
                  placeholder={t('addDelegate.emailPlaceholder')}
                  label={t('addDelegate.email')}
                  autoComplete='off'
                  hasError={!!formErrors.email && !!touchedFields.email}
                />
              </Box>
            </Box>
            <Box
              width='100%'
              flex={0}
              alignItems='flex-end'
              justifyContent='flex-end'
              display='flex'
            >
              <Button
                data-testid='add-delegate-add-button'
                variant='primary'
                minWidth='none'
                px='xs'
                spinnerColor='white'
                disabled={!isValid || !someInputFilled}
                onClick={handleSubmit(onAdd)}
              >
                <Typography variant='p2'>{t('addDelegate.cta.add')}</Typography>
              </Button>
            </Box>
          </Box>
          {formErrors.email?.type === 'pattern' && touchedFields.email && (
            <Box mt='xs'>
              <AlertBadge type='error' iconName='info' label={t('addDelegate.alertInvalidEmail')} />
            </Box>
          )}
          <AlertBadge
            containerProps={{ mt: 'm' }}
            type='transparent'
            iconName='info'
            label={
              <Typography variant='p2/light'>
                <RenderLocalesWithComponents
                  text={t('addDelegate.infoBox', {
                    downloadFile: '!{DOCS}!',
                  })}
                  placeholders={{
                    '!{DOCS}!': (
                      <Typography
                        as='span'
                        fontWeight='500'
                        style={{
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          color: 'inherit',
                        }}
                        onClick={goToDocs}
                      >
                        {t('addDelegate.labelDocs')}
                      </Typography>
                    ),
                    '!{CONTACT}!': (
                      <Typography
                        as='span'
                        fontWeight='500'
                        style={{
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          color: 'inherit',
                        }}
                        onClick={goToContact}
                      >
                        {t('addDelegate.labelContact')}
                      </Typography>
                    ),
                  }}
                />
              </Typography>
            }
          />
          {/* On desktop the wizard navigation lives here, right below the form,
              so it stays close to it instead of slipping to the bottom of a long
              delegates list. Mobile and tablet keep the automatic footer.
              Skip mirrors the automatic footer visibility: it shows only while no
              delegate has been added yet (showSkipButton: hrDelegates.length === 0). */}
          {isDesktop && (
            <Box mt='m' display='flex' style={{ gap: gapXS }}>
              <wizardNavigationButton.Next />
              {hrDelegates.length === 0 && <wizardNavigationButton.Skip />}
            </Box>
          )}
        </Box>
        <AddedDelegatesList />
      </Box>
    </Box>
  );
};
